import { KeyboardEvent, PointerEvent, useEffect, useMemo, useRef, useState } from "react";
import MirrorActivityLayout from "../components/MirrorActivityLayout";
import { useTranslation } from "../components/Localization";
import { useNavigate } from "react-router-dom";
import { resolveTourPath, useTourMode } from "../components/TourMode";
import { saveGameScore } from "../services/gameScores";
import { usePageGame } from "../hooks/usePageGame";

type Props = { variant?: 1 | 2 };
const OPTION_WIDTH = 265;

const paintingNavigation = {
  1: { backTo: "/tukorterem/konzol", nextTo: "/tukorterem/festmeny-2", step: 4 },
  2: { backTo: "/tukorterem/festmeny", nextTo: "/tukorterem/gyuruk", step: 5 },
} as const;

export function paintingScoreFromAttempts(attempts: number) {
  if (attempts <= 1) return 10;
  if (attempts === 2) return 8;
  if (attempts === 3) return 6;
  if (attempts === 4) return 4;
  return 2;
}

export default function MirrorPaintingGamePage({ variant = 1 }: Props) {
  const { room, t } = useTranslation();
  const { interactive } = useTourMode();
  const { game } = usePageGame("tukorterem", "match_text_image");
  const navigate = useNavigate();
  const [optionIndex, setOptionIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [result, setResult] = useState<"correct" | "incorrect" | null>(null);
  const [score, setScore] = useState(0);
  const dragStart = useRef<number | null>(null);
  const pendingDirection = useRef<-1 | 1 | null>(null);
  const navigation = paintingNavigation[variant];
  const painting = useMemo(() => {
    const items = game?.items.filter((item) => item.roundIndex === variant - 1) || [];
    const image = items.find((item) => item.kind === "image" && item.url)?.url || "";
    const options = items.filter((item) => item.kind === "text" && item.textContent).map((item) => ({ text: item.textContent as string, correct: item.isCorrect === true }));
    return { image, options };
  }, [game, variant]);
  const selectedOption = painting.options[optionIndex];
  const correct = selectedOption?.correct === true;

  useEffect(() => {
    setOptionIndex(0);
    setDragOffset(0);
    setAnimating(false);
    setAttempts(0);
    setResult(null);
    setScore(0);
    pendingDirection.current = null;
  }, [game, variant]);

  const optionAt = (offset: number) => painting.options.length ? painting.options[(optionIndex + offset + painting.options.length) % painting.options.length].text : "";

  const animateToOption = (direction: -1 | 1) => {
    if (animating) return;
    pendingDirection.current = direction;
    setAnimating(true);
    setDragOffset(-direction * OPTION_WIDTH);
  };

  const finishAnimation = () => {
    const direction = pendingDirection.current;
    setAnimating(false);
    setDragOffset(0);
    if (direction !== null) {
      setOptionIndex((current) => painting.options.length ? (current + direction + painting.options.length) % painting.options.length : 0);
    }
    pendingDirection.current = null;
  };

  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (animating) return;
    dragStart.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStart.current === null) return;
    setDragOffset(Math.max(-80, Math.min(80, event.clientX - dragStart.current)));
  };

  const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStart.current === null) return;
    const distance = event.clientX - dragStart.current;
    dragStart.current = null;
    if (Math.abs(distance) >= 35) {
      animateToOption(distance < 0 ? 1 : -1);
    } else {
      pendingDirection.current = null;
      setAnimating(distance !== 0);
      setDragOffset(0);
    }
  };

  const selectWithKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      animateToOption(event.key === "ArrowRight" ? 1 : -1);
    }
  };

  const checkAnswer = () => {
    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);
    if (!correct) {
      setResult("incorrect");
      return;
    }

    const earnedScore = paintingScoreFromAttempts(nextAttempts);
    saveGameScore(variant === 1 ? "paintingOne" : "paintingTwo", earnedScore);
    setScore(earnedScore);
    setResult("correct");
  };

  const continueTour = () => {
    navigate(resolveTourPath(navigation.nextTo, 1, interactive));
  };

  return <MirrorActivityLayout
    instruction={room("mirror")?.tasks.painting || ""}
    taskKey="painting"
    pageType="game"
    step={navigation.step}
    backTo={navigation.backTo}
    nextTo={navigation.nextTo}
    showProgressIcon={false}
    onCheck={checkAnswer}
  >
    <div className="mirror-painting">
      <div className="mirror-painting-placeholder">{painting.image && <img src={painting.image} alt={t("accessibility.painting", { variant })} />}</div>
      <div className="mirror-painting-options-viewport">
        <div className={`mirror-painting-options-track ${animating ? "is-animating" : ""}`} style={{ transform: `translateX(calc(-${OPTION_WIDTH}px + ${dragOffset}px))` }} role="listbox" tabIndex={0} aria-label={t("accessibility.choosePaintingName")} aria-activedescendant={`painting-option-${variant}-${optionIndex}`} onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={finishDrag} onPointerCancel={() => { dragStart.current = null; pendingDirection.current = null; setAnimating(dragOffset !== 0); setDragOffset(0); }} onTransitionEnd={finishAnimation} onKeyDown={selectWithKeyboard}>
          <div className="mirror-painting-option" role="option" aria-selected="false">{optionAt(-1)}</div>
          <div className="mirror-painting-option" id={`painting-option-${variant}-${optionIndex}`} role="option" aria-selected="true">{selectedOption?.text || ""}</div>
          <div className="mirror-painting-option" role="option" aria-selected="false">{optionAt(1)}</div>
        </div>
      </div>
      <span className="mirror-painting-feedback">{t("accessibility.paintingHint")}</span>
    </div>
    {result && <div className="painting-result-backdrop">
      <section className={`painting-result-modal is-${result}`} role="dialog" aria-modal="true" aria-labelledby="painting-result-title">
        <h2 id="painting-result-title">{t(result === "correct" ? "paintingGame.correctTitle" : "paintingGame.incorrectTitle")}</h2>
        <p>{t("paintingGame.attempts", { count: attempts })}</p>
        {result === "correct" && <strong>{t("score.gameResult", { score, max: 10 })}</strong>}
        <button type="button" onClick={result === "correct" ? continueTour : () => setResult(null)}>
          {t(result === "correct" ? "common.next" : "escapeGame.retry")}
        </button>
      </section>
    </div>}
  </MirrorActivityLayout>;
}
